// ---------------------------------------------------------------------------
// Project BOQ from the customer's PO: the PDF's text (with positions) is turned into BOQ lines, without any
// outside service. Works for PDFs made by a computer (text can be selected); scanned PDFs have no text, so the
// project manager pastes the lines from Excel instead. The project manager always checks the lines before saving.
// ---------------------------------------------------------------------------
import { z } from 'zod';

/** One piece of text on a PDF page; `top` grows downwards from the top of the page. */
export interface PdfTextItem { page: number; x: number; top: number; w: number; str: string }

export interface BoqDraftLine {
  lineNo: string | null;
  description: string;
  brand: string | null;
  model: string | null;
  unit: string | null;
  qty: number | null;
  unitPrice: number | null;
  total: number | null;
}
export interface BoqDraft {
  lines: BoqDraftLine[];
  poNumber: string | null;
  /** YYYY-MM-DD */
  poDate: string | null;
  /** The PO's own total, to compare with the lines. */
  poTotal: number | null;
  /** How the lines were found: a table with column titles, line patterns, or nothing. */
  method: 'table' | 'pattern' | 'none';
}

type Field = keyof BoqDraftLine;
interface Cell { x: number; x2: number; text: string }
interface Row { page: number; top: number; cells: Cell[] }

const ARABIC_DIGITS = /[٠-٩]/g;
/** "1,234.50", "SAR 1,234", "١٢٣" → number; anything else → null. */
export function parseBoqNumber(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = s.replace(ARABIC_DIGITS, (d) => String(d.charCodeAt(0) - 0x660)).replace(/٫/g, '.').replace(/٬/g, ',')
    .replace(/SAR|S\.R\.?|SR|ر\.س\.?|ريال/gi, '').replace(/[\s,]/g, '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** What a column title means. */
export function classifyBoqHeader(text: string): Field | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return null;
  if (/total|amount|ext\.? ?price|الإجمالي|الاجمالي|المبلغ|المجموع|القيمة/.test(t)) return 'total';
  if (/unit ?(price|rate|cost)|^rate$|^price$|سعر|السعر/.test(t)) return 'unitPrice';
  if (/qty|quantity|qnty|الكمية|العدد/.test(t)) return 'qty';
  if (/^(unit|units|uom|u\/m|u\.o\.m\.?)$|الوحدة/.test(t)) return 'unit';
  if (/brand|make|manufacturer|الماركة|العلامة/.test(t)) return 'brand';
  if (/model|part ?(no|number|#)|p\/n|الموديل|الطراز/.test(t)) return 'model';
  if (/desc|particular|scope|details|specification|الوصف|البيان/.test(t)) return 'description';
  if (/^(s\.? ?no\.?|sr\.? ?no\.?|sl\.? ?no\.?|item ?(no\.?|#|code)?|no\.?|#|م|رقم|الرقم|البند|ref\.?)$/.test(t)) return 'lineNo';
  return null;
}

const TOTAL_ROW = /^(sub ?-?total|grand total|total|net total|net amount|vat|value added tax|discount|المجموع|الإجمالي|الاجمالي|ضريبة|الخصم)\b/i;

/** Puts text pieces on the same line together and joins pieces that touch into cells. */
function toRows(items: PdfTextItem[]): Row[] {
  const sorted = items.filter((i) => i.str.trim()).sort((a, b) => a.page - b.page || a.top - b.top || a.x - b.x);
  const rows: Row[] = [];
  for (const it of sorted) {
    let row = rows[rows.length - 1];
    if (!row || row.page !== it.page || Math.abs(row.top - it.top) > 3) {
      row = { page: it.page, top: it.top, cells: [] };
      rows.push(row);
    }
    row.cells.push({ x: it.x, x2: it.x + Math.max(it.w, 1), text: it.str });
  }
  for (const r of rows) {
    r.cells.sort((a, b) => a.x - b.x);
    const merged: Cell[] = [];
    for (const c of r.cells) {
      const prev = merged[merged.length - 1];
      if (prev && c.x - prev.x2 <= 4) {
        prev.text += (c.x - prev.x2 > 1 && !prev.text.endsWith(' ') && !c.text.startsWith(' ') ? ' ' : '') + c.text;
        prev.x2 = Math.max(prev.x2, c.x2);
      } else merged.push({ ...c });
    }
    r.cells = merged.map((c) => ({ ...c, text: c.text.replace(/\s+/g, ' ').trim() })).filter((c) => c.text);
  }
  return rows;
}

interface Column { field: Field; center: number; from: number; to: number }

/** The column titles row: at least three known titles, including the quantity. */
function headerOf(row: Row): Column[] | null {
  const found: { field: Field; center: number }[] = [];
  for (const c of row.cells) {
    const f = classifyBoqHeader(c.text);
    if (f && !found.some((x) => x.field === f)) found.push({ field: f, center: (c.x + c.x2) / 2 });
  }
  if (found.length < 3 || !found.some((f) => f.field === 'qty')) return null;
  // "Item" alone is the description when there's no other description column.
  if (!found.some((f) => f.field === 'description')) {
    const item = row.cells.find((c) => /^item$/i.test(c.text.trim()));
    const ln = found.find((f) => f.field === 'lineNo');
    if (item && ln) ln.field = 'description';
    else return null;
  }
  found.sort((a, b) => a.center - b.center);
  return found.map((f, i) => ({
    field: f.field, center: f.center,
    from: i === 0 ? -Infinity : (found[i - 1]!.center + f.center) / 2,
    to: i === found.length - 1 ? Infinity : (f.center + found[i + 1]!.center) / 2,
  }));
}

function emptyLine(): BoqDraftLine {
  return { lineNo: null, description: '', brand: null, model: null, unit: null, qty: null, unitPrice: null, total: null };
}

/** Fills a line from a row's cells, each cell going to the column it overlaps most. */
function lineFrom(row: Row, cols: Column[]): Partial<Record<Field, string>> {
  const out: Partial<Record<Field, string>> = {};
  for (const c of row.cells) {
    let best: Column | null = null;
    let bestScore = -Infinity;
    for (const col of cols) {
      const overlap = Math.min(c.x2, col.to) - Math.max(c.x, col.from);
      const score = overlap > 0 ? overlap : -Math.abs((c.x + c.x2) / 2 - col.center);
      if (score > bestScore) { bestScore = score; best = col; }
    }
    if (best) out[best.field] = out[best.field] ? `${out[best.field]} ${c.text}` : c.text;
  }
  return out;
}

function finish(l: BoqDraftLine): BoqDraftLine {
  if (l.total == null && l.qty != null && l.unitPrice != null) l.total = Math.round(l.qty * l.unitPrice * 100) / 100;
  if (l.unitPrice == null && l.qty && l.total != null) l.unitPrice = Math.round((l.total / l.qty) * 100) / 100;
  l.description = l.description.trim();
  return l;
}

function fromTable(rows: Row[]): BoqDraftLine[] {
  const lines: BoqDraftLine[] = [];
  let cols: Column[] | null = null;
  let open = false;
  let lastTop = 0;
  let lastPage = 0;
  for (const row of rows) {
    const h = headerOf(row);
    if (h) { cols = h; open = true; lastTop = row.top; lastPage = row.page; continue; }
    if (!cols) continue;
    const first = row.cells[0]?.text ?? '';
    const v = lineFrom(row, cols);
    if (TOTAL_ROW.test(first) || TOTAL_ROW.test(v.description ?? '')) { open = false; continue; }
    const qty = parseBoqNumber(v.qty);
    const price = parseBoqNumber(v.unitPrice);
    const total = parseBoqNumber(v.total);
    if (qty != null && (v.description || v.lineNo || v.model)) {
      lines.push(finish({
        ...emptyLine(), lineNo: v.lineNo ?? null, description: v.description ?? v.model ?? '', brand: v.brand ?? null, model: v.model ?? null,
        unit: v.unit ?? null, qty, unitPrice: price, total,
      }));
      open = true;
    } else if (open && lines.length && v.description && qty == null && price == null && total == null
      && (row.page !== lastPage || row.top - lastTop < 40)) {
      // A description that continues on the next line.
      const prev = lines[lines.length - 1]!;
      prev.description = `${prev.description} ${v.description}`.trim();
      if (v.brand && !prev.brand) prev.brand = v.brand;
      if (v.model && !prev.model) prev.model = v.model;
    } else if (qty == null && price == null && total == null && !v.description) {
      continue;
    }
    lastTop = row.top;
    lastPage = row.page;
  }
  return lines;
}

const NUM = String.raw`(-?[\d٠-٩][\d٠-٩,]*(?:\.\d+)?)`;
const PATTERN = new RegExp(String.raw`^(?:(\d+(?:\.\d+)*)[.)]?\s+)?(.+?)\s+${NUM}\s+(?:([A-Za-z؀-ۿ.]{1,8})\s+)?${NUM}\s+${NUM}$`);

/** Without column titles: lines ending in "qty [unit] unit-price total" where qty × price ≈ total. */
function fromPatterns(rows: Row[]): BoqDraftLine[] {
  const lines: BoqDraftLine[] = [];
  for (const row of rows) {
    const text = row.cells.map((c) => c.text).join(' ');
    const m = PATTERN.exec(text);
    if (!m) continue;
    const qty = parseBoqNumber(m[3]);
    const price = parseBoqNumber(m[5]);
    const total = parseBoqNumber(m[6]);
    if (qty == null || price == null || total == null || qty <= 0) continue;
    if (Math.abs(qty * price - total) > Math.max(1, total * 0.01)) continue;
    lines.push(finish({ ...emptyLine(), lineNo: m[1] ?? null, description: m[2]!.trim(), unit: m[4] ?? null, qty, unitPrice: price, total }));
  }
  return lines;
}

/**
 * The PO number: "PO No / Purchase Order Number / LPO # …" (a word on its own followed by a number marker, so
 * "Corporate", "Deposit" or "P.O. Box" are never taken), or the Arabic "أمر الشراء رقم …".
 */
function findPoNumber(text: string): string | null {
  const en = /\b(?:P\.?\s?O\.?|Purchase\s+Order|LPO)\s*(?:No\.?|Number|#|Ref\.?)\s*[:\-#]?\s*(?!box\b)([A-Z0-9][A-Z0-9\-/_.]{2,30})/i.exec(text);
  const ar = en ? null : /(?:أمر\s+الشراء|امر\s+شراء)\s*(?:رقم)?\s*[:\-#]?\s*([A-Z0-9][A-Z0-9\-/_.]{2,30})/i.exec(text);
  const m = en ?? ar;
  return m ? m[1]!.replace(/[.\-/]+$/, '') : null;
}

function findDate(text: string): string | null {
  const iso = /\b(20\d{2})[-/.](\d{1,2})[-/.](\d{1,2})\b/.exec(text);
  const dmy = /\b(\d{1,2})[-/.](\d{1,2})[-/.](20\d{2})\b/.exec(text);
  const pick = (y: number, mo: number, d: number) => (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 ? `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}` : null);
  if (dmy && (!iso || dmy.index < iso.index)) return pick(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
  if (iso) return pick(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  return null;
}

function findTotal(rows: Row[]): number | null {
  let best: number | null = null;
  for (const r of rows) {
    const text = r.cells.map((c) => c.text).join(' ');
    if (!/grand total|total amount|net total|total including vat|total incl|net amount|الإجمالي|الاجمالي|المجموع الكلي/i.test(text)) continue;
    for (const c of r.cells) {
      const n = parseBoqNumber(c.text);
      if (n != null && n > 0 && (best == null || n > best)) best = n;
    }
  }
  return best;
}

/** Reads BOQ lines and the PO number, date and total from the text of a PO PDF. */
export function parsePoText(items: PdfTextItem[]): BoqDraft {
  const rows = toRows(items);
  const allText = rows.map((r) => r.cells.map((c) => c.text).join(' ')).join('\n');
  let lines = fromTable(rows);
  let method: BoqDraft['method'] = 'table';
  if (!lines.length) { lines = fromPatterns(rows); method = 'pattern'; }
  if (!lines.length) method = 'none';
  return { lines, poNumber: findPoNumber(allText), poDate: findDate(allText), poTotal: findTotal(rows), method };
}

/** Lines copied from Excel (tab-separated), with or without the column titles row. */
export function parsePastedBoq(text: string): BoqDraftLine[] {
  const rows = text.split(/\r?\n/).map((l) => l.split('\t').map((c) => c.trim())).filter((r) => r.some(Boolean));
  if (!rows.length) return [];
  const fields = rows[0]!.map((c) => classifyBoqHeader(c));
  const hasHeader = fields.filter(Boolean).length >= 2 && fields.includes('qty');
  const out: BoqDraftLine[] = [];
  for (const r of hasHeader ? rows.slice(1) : rows) {
    const l = emptyLine();
    if (hasHeader) {
      r.forEach((c, i) => {
        const f = fields[i];
        if (!f || !c) return;
        if (f === 'qty' || f === 'unitPrice' || f === 'total') l[f] = parseBoqNumber(c);
        else if (f === 'description') l.description = l.description ? `${l.description} ${c}` : c;
        else l[f] = c;
      });
    } else {
      // No titles: numbers from the right are total, unit price, quantity; the longest text is the description.
      const nums = r.map((c, i) => ({ i, n: parseBoqNumber(c) })).filter((x) => x.n != null);
      const texts = r.map((c, i) => ({ i, c })).filter((x) => x.c && parseBoqNumber(x.c) == null);
      const desc = texts.sort((a, b) => b.c.length - a.c.length)[0];
      if (!desc) continue;
      l.description = desc.c;
      const right = nums.filter((x) => x.i > desc.i);
      if (right.length >= 3) { l.qty = right[0]!.n; l.unitPrice = right[1]!.n; l.total = right[2]!.n; } else if (right.length === 2) { l.qty = right[0]!.n; l.unitPrice = right[1]!.n; } else if (right.length === 1) l.qty = right[0]!.n;
      const first = r[0] ?? '';
      if (desc.i > 0 && first && first.length <= 8) l.lineNo = first;
      const unit = texts.find((x) => x.i > desc.i && x.c.length <= 8);
      if (unit) l.unit = unit.c;
    }
    if (l.description || l.qty != null) out.push(finish(l));
  }
  return out;
}

// --- Saving -----------------------------------------------------------------------------------------

const optText = (max: number) => z.preprocess((v) => (v === '' || v === undefined ? null : v), z.string().trim().max(max).nullable());
const optNum = z.preprocess((v) => (v === '' || v === undefined ? null : v), z.number().finite().min(0).max(1e12).nullable());

export const boqLineInputSchema = z.object({
  lineNo: optText(40),
  description: z.string().trim().min(1, 'Description is required').max(4000),
  system: optText(40),
  brand: optText(120),
  model: optText(120),
  unit: optText(30),
  qty: optNum,
  unitPrice: optNum,
  total: optNum,
});

export const boqImportSchema = z.object({
  /** Add after the current lines, or replace them all. */
  mode: z.enum(['append', 'replace']),
  /** The file the lines came from (shown on each line). */
  source: optText(200),
  /** Filled into the project when it has none yet. */
  poNumber: optText(100),
  poDate: z.preprocess((v) => (v === '' || v === undefined ? null : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable()),
  lines: z.array(boqLineInputSchema).min(1, 'Add at least one line').max(3000),
}).strict();
export type BoqImport = z.infer<typeof boqImportSchema>;
